import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../../BlocksElement';
import styles from '../icon.styles';
import type { IconColorType, IconSize } from './icon.model';

/**
 * Search component
 * @csspart base- The component's base wrapper
 */
export class BlocksIconSearch extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Icon color variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders an icon with the currentColor of its parent element</li>
   * <li>**content-strong** renders an icon with the cat-theme-color-content-strong color token</li>
   * <li>**knockout** renders an icon with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders an icon with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**information** renders an icon with the cat-theme-color-content-utility-information color token</li>
   * <li>**success** renders an icon with the cat-theme-color-content-utility-success color token</li>
   * <li>**warning** renders a renders an icon with the cat-theme-color-content-utility-warning color token</li>
   * <li>**error** renders an icon with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  color: IconColorType;

  /**
   * Title
   * 1) Icons should always have associated text. Use this to supply `aria-label` to icon if it stands by itself without any visual/visually-hidden text
   */
  @property({ type: String })
  iconTitle: string | undefined;

  /**
   * Size property
   * - Default size is 16px
   * - **sm** renders a larger size than default (12px)
   * - **lg** renders a larger size than default (24px)
   * - **xl** renders a larger size than the lg variant (32px);
   */
  @property({ type: String })
  size: IconSize;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('cat-icon');
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-icon', {
      'cat-c-icon--sm': this.size === 'sm',
      'cat-c-icon--lg': this.size === 'lg',
      'cat-c-icon--xl': this.size === 'xl',
      'cat-c-icon--content-strong': this.color === 'content-strong',
      'cat-c-icon--knockout': this.color === 'knockout',
      'cat-c-icon--knockout-brand': this.color === 'knockout-brand',
      'cat-c-icon--information': this.color === 'information',
      'cat-c-icon--success': this.color === 'success',
      'cat-c-icon--warning': this.color === 'warning',
      'cat-c-icon--error': this.color === 'error'
    });

    return html`
      <span part="base" class="${componentClassName}" role="presentation">
        <svg
          aria-hidden="${!this.iconTitle}"
          aria-label="${ifDefined(this.iconTitle)}"
          role="${this.iconTitle && 'img'}"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M2.60547 10.3147C2.60547 6.05702 6.05702 2.60547 10.3147 2.60547C14.5724 2.60547 18.024 6.05702 18.024 10.3147C18.024 12.4113 17.1871 14.3124 15.8291 15.7022C15.8059 15.7204 15.7836 15.7403 15.7622 15.7617C15.7407 15.7831 15.7209 15.8055 15.7026 15.8287C14.3128 17.1869 12.4115 18.024 10.3147 18.024C6.05702 18.024 2.60547 14.5724 2.60547 10.3147ZM16.2751 17.3353C14.6688 18.7004 12.5879 19.524 10.3147 19.524C5.2286 19.524 1.10547 15.4009 1.10547 10.3147C1.10547 5.2286 5.2286 1.10547 10.3147 1.10547C15.4009 1.10547 19.524 5.2286 19.524 10.3147C19.524 12.5877 18.7006 14.6683 17.3357 16.2746L22.6756 21.6145C22.9685 21.9074 22.9685 22.3822 22.6756 22.6751C22.3827 22.968 21.9079 22.968 21.615 22.6751L16.2751 17.3353Z"
          />
        </svg>
      </span>
    `;
  }
}

if (customElements.get('cat-icon-search') === undefined) {
  customElements.define('cat-icon-search', BlocksIconSearch);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-icon-search': BlocksIconSearch;
  }
}
